import { AdapterFactory } from "@home-health-notify/adapters";
import { createSessionManager } from "@home-health-notify/auth";
import { createInitialDomain, IDomain } from "@home-health-notify/domain";
import CloseIcon from "@mui/icons-material/Close";
import { CssBaseline, IconButton, ThemeProvider } from "@mui/material";
import * as Sentry from "@sentry/react";
import axios from "axios";
import {
  DeveloperSettingsProvider,
  DomainProvider,
  InternetStatusProvider,
  OrganizationProvider,
} from "hhn-commons";
import { SnackbarKey, SnackbarProvider, useSnackbar } from "notistack";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router } from "react-router";
import { initializeFirebaseAnalytics } from "./analytics/firebase";
import App from "./App";
import { MultiTabAuthSync } from "./auth/MultiTabAuthSync";
import { internetStatusListener } from "./connection/InternetStatusListener";
import { AuthProviderWrapper } from "./context/AuthProviderWrapper";
import { ErrorBoundary } from "./ErrorBoundary";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import theme from "./theme";
import config from "./utils/Config";
import { organizationIdStorage } from "./utils/organizationIdStorage";
import { outboxStore } from "./utils/outboxStore";
import { isHandledHttpClientFailureEvent } from "./utils/sentryFilters";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

function SnackbarCloseAction({ snackbarKey }: { snackbarKey: SnackbarKey }) {
  const { closeSnackbar } = useSnackbar();
  return (
    <IconButton
      size="small"
      aria-label="Dismiss"
      onClick={() => closeSnackbar(snackbarKey)}
      sx={{ color: "inherit" }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );
}

const amplify = {
  Auth: {
    Cognito: {
      userPoolId: config.userPoolId,
      userPoolClientId: config.userPoolWebClientId,
      loginWith: {
        oauth: {
          domain: config.cognitoOAuthDomain,
          // `aws.cognito.signin.user.admin` is required for the access
          // token to call Cognito user-pool API operations (ChangePassword,
          // UpdateUserAttributes, GetUser). Without it those endpoints
          // return `NotAuthorizedException: Access Token does not have
          // required scopes`. Scope must also be allowed on the Cognito
          // App Client (OpenID Connect scopes section).
          scopes: [
            "openid",
            "email",
            "profile",
            "aws.cognito.signin.user.admin",
          ],
          redirectSignIn: [config.cognitoRedirectSignIn],
          redirectSignOut: [config.cognitoRedirectSignOut],
          responseType: "code" as const,
        },
      },
    },
  },
  // Opt into cookie-based token storage so all *.<env>.homehealthnotify.com
  // apps share auth state. When REACT_APP_cookieDomain is unset, this stays
  // undefined and Amplify falls back to its default localStorage.
  cookieStorage: config.cookieDomain
    ? {
        domain: config.cookieDomain,
        // Local dev runs on http://localhost — secure: true cookies would
        // be silently rejected by the browser. Only override secure for
        // the localhost case; all other envs use the prod-safe default.
        ...(config.cookieDomain === "localhost" && { secure: false }),
      }
    : undefined,
};

function clearOldRootDomainCookies() {
  // Other apps under the shared cookie domain (e.g. the old form-login app on
  // qa vs. this hosted-UI app on beta.qa) write Cognito cookies keyed by a
  // DIFFERENT Cognito app-client id. The browser sends every one of them on
  // each request, eventually overflowing nginx's header buffer (HTTP 400).
  // Delete any Cognito cookie whose client id isn't ours; ours is skipped so
  // this stays safe to run on every boot without dropping the live session.
  const myPrefix = `CognitoIdentityServiceProvider.${config.userPoolWebClientId}.`;

  // document.cookie never exposes a cookie's Domain, so expire each foreign
  // cookie on every parent scope the current host could have used. Deriving
  // these from the hostname (stopping before the TLD) keeps it correct per-env
  // — qa, beta.qa, prod, localhost — with no hardcoded domain.
  const labels = window.location.hostname.split(".");
  const scopes: string[] = [];
  for (let i = 0; i < labels.length - 1; i++) {
    scopes.push("." + labels.slice(i).join("."));
  }

  document.cookie.split(";").forEach(cookie => {
    const name = cookie.split("=")[0].trim();
    if (
      name.startsWith("CognitoIdentityServiceProvider.") &&
      !name.startsWith(myPrefix)
    ) {
      console.log("Deleting foreign Cognito cookie:", name);
      const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = base; // host-only
      scopes.forEach(scope => {
        document.cookie = `${base}; domain=${scope}`;
      });
    }
  });
}

clearOldRootDomainCookies();

// Construct the auth subsystem once at boot via the package's factory.
// The factory hides the underlying identity-provider choice (Amplify today).
// The SessionManager's internal adapter is shared with AdapterFactory so HTTP
// token attachment and React-side auth state run off the same instance.
const sessionManager = createSessionManager(amplify);
const adapterFactory = new AdapterFactory(config, sessionManager, axios);
/*const domain: IDomain = new Domain(
  adapterFactory,
  config,
  internetStatusListener,
);*/

const domain: IDomain = createInitialDomain(
  adapterFactory,
  config,
  internetStatusListener,
  organizationIdStorage,
  undefined,
  outboxStore,
);

// Defer Firebase Analytics init until the network is confirmed online.
domain.internetStatusManager.waitForOnline().then(() => {
  initializeFirebaseAnalytics();
});

// Defense in depth against a pasted EMR document (PHI) reaching Sentry via an
// event's message or exception text. The EMR paste path (packages/emr-parser)
// only engages on blobs >500 chars with >10 newlines, so anything matching that
// shape in event text is almost certainly a patient document, not a real error
// string. Bias is toward dropping: we would rather lose a legitimate error than
// leak one paste (no BAA with Sentry).
function looksLikeEmrPaste(text: unknown): boolean {
  if (typeof text !== "string" || text.length < 200) return false;
  // Shape signal: long + many line breaks (mirrors the parser's own trigger,
  // with margin so ordinary multi-line errors/stack traces don't match).
  const newlines = (text.match(/\n/g) || []).length;
  if (text.length >= 1000 && newlines >= 15) return true;
  // Content signal: several EMR section landmarks co-occurring in one string is
  // not something a normal error message or stack trace produces.
  const landmarks = [
    "Patient Information",
    "Emergency Contact",
    "Primary Physician",
    "Insurance",
    "Pharmacy",
    "Date of Birth",
    "Medicaid",
    "Medicare",
    "Allergies",
    "Referral Source",
  ];
  const hits = landmarks.filter(landmark => text.includes(landmark)).length;
  return hits >= 3;
}

function eventContainsEmrPaste(event: Sentry.ErrorEvent): boolean {
  if (looksLikeEmrPaste(event.message)) return true;
  for (const value of event.exception?.values ?? []) {
    if (looksLikeEmrPaste(value.value)) return true;
  }
  return false;
}

//NODE_ENV === "production" is both QA and PROD
if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // HIPAA: these masking flags are load-bearing for compliance — we hold
      // no BAA with Sentry, so no PHI may reach Session Replay. All three are
      // set explicitly, including where they match the current SDK default, so
      // an SDK upgrade cannot silently flip masking off. Do not relax without
      // compliance sign-off.
      //
      // HIPAA/PHI (client-accepted 2026-08-26): network capture below records
      // FULL request/response bodies for backend URLs = patient PHI to Sentry,
      // and maskAllText does NOT cover this (separate channel). No BAA with
      // Sentry; client explicitly accepted this exposure for debugging value.
      // See docs/sentry-phi-network-capture-decision.md
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [
          config.baseUrl,
          "https://www.gstatic.com/generate_204",
        ],
        networkCaptureBodies: true,
        networkRequestHeaders: ["x-hhn-active-org-id"],
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[400, 599]],
        failedRequestTargets: [config.baseUrl],
      }),
      Sentry.httpContextIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.feedbackIntegration({
        // Additional SDK configuration goes in here, for example:
        colorScheme: "dark",
        triggerLabel: "Share feedback",
        submitButtonLabel: "Submit feedback",
        formTitle: "Share your feedback",
        messagePlaceholder: "What you want to share?",
        successMessageText: "Thank you for your feedback!",
        showBranding: false,
        autoInject: false,
      }),
    ],
    environment: config.env,
    // Sample 100% of errors for testing
    sampleRate: 1.0,

    // Don't send personally identifiable information
    sendDefaultPii: true,

    // Limit how many actions we track before an error
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (error instanceof Error && (error as any).code === "OFFLINE_TIMEOUT") {
        return null;
      }
      if (
        error instanceof Error &&
        (error as any).code === "installations/app-offline"
      ) {
        return null;
      }
      // Drop httpClientIntegration captures of failures the app already handles.
      if (isHandledHttpClientFailureEvent(event)) {
        return null;
      }
      // PHI safety net: never send an event whose text looks like a pasted EMR
      // document (no BAA with Sentry).
      if (eventContainsEmrPaste(event)) {
        return null;
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === "xhr") {
        const url = breadcrumb.data?.url || "";
        if (!url.includes(config.baseUrl)) {
          return breadcrumb;
        }
        const xhr = hint?.xhr as XMLHttpRequest | undefined;
        if (xhr) {
          const tryParseJSON = (input: unknown) => {
            if (typeof input !== "string" || input.length > 2000)
              return undefined;
            try {
              return JSON.parse(input);
            } catch {
              return input;
            }
          };
          const requestBody = tryParseJSON(hint?.input);
          if (requestBody && typeof requestBody === "object") {
            delete requestBody.password;
            delete requestBody.token;
          }
          breadcrumb.data = {
            ...breadcrumb.data,
            ...(requestBody && { requestBody }),
            ...(xhr.responseText && {
              responseBody: tryParseJSON(xhr.responseText),
            }),
          };
        }
      }
      return breadcrumb;
    },
  });
}
root.render(
  <Router>
    <ErrorBoundary domain={domain}>
      <DomainProvider domain={domain}>
        <InternetStatusProvider
          internetStatusManager={domain.internetStatusManager}
        >
          <OrganizationProvider>
            <DeveloperSettingsProvider>
              <AuthProviderWrapper sessionManager={sessionManager}>
                <ThemeProvider theme={theme}>
                  <SnackbarProvider
                    maxSnack={3}
                    style={{
                      width: "400px",
                    }}
                    action={key => <SnackbarCloseAction snackbarKey={key} />}
                  >
                    <CssBaseline />

                    <App />
                    <MultiTabAuthSync />
                  </SnackbarProvider>
                </ThemeProvider>
              </AuthProviderWrapper>
            </DeveloperSettingsProvider>
          </OrganizationProvider>
        </InternetStatusProvider>
      </DomainProvider>
    </ErrorBoundary>
  </Router>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
